import React, { useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  RefreshControl,
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { useFocusEffect } from '@react-navigation/native';
import { formatDistance, parseISO } from 'date-fns';
import Icon from 'react-native-vector-icons/Ionicons';

import { RootState, AppDispatch } from '@/store';
import {
  getNotifications,
  getUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
} from '@/store/slices/notificationsSlice';
import { dispatchNotificationRoute } from '@/services/notificationRouter';
import { NotificationItem } from '@/types/api';
import { colors, font, space, radius, shadow } from '@/theme/chalkCourt';

const iconFor = (type: string): string => {
  switch (type) {
    case 'TIMEOFF_SUB':
      return 'swap-horizontal';
    case 'TIMEOFF_REASSIGN':
      return 'alert-circle';
    case 'TIMEOFF_SUBMITTED':
      return 'calendar';
    case 'TIMEOFF_APPROVED':
      return 'checkmark-circle';
    case 'TIMEOFF_DENIED':
      return 'close-circle';
    case 'TIMEOFF_SUB_UNASSIGNED':
      return 'person-remove';
    case 'TIMEOFF_CANCELLED':
      return 'ban';
    case 'TIMEOFF_REMINDER':
      return 'alarm';
    case 'TIMEOFF_ESCALATION':
      return 'warning';
    case 'TIMEOFF_PICK_CLASSES':
      return 'list';
    default:
      return 'notifications';
  }
};

/**
 * "5 minutes ago". The API sends createdOn as a UTC instant (`…Z`), which parseISO
 * reads correctly; any timestamp in the future (clock skew, or an older zone-less
 * value read as local time) shows "just now" rather than "in …".
 */
export const relTime = (iso: string, now: Date = new Date()): string => {
  try {
    const at = parseISO(iso);
    if (at.getTime() > now.getTime()) {
      return 'just now';
    }
    return formatDistance(at, now, { addSuffix: true });
  } catch {
    return '';
  }
};

/**
 * Notification center — the staff member's in-app notification history
 * (`/notifications`). Tapping a time-off notification marks it read and routes
 * via the shared notificationRouter (same deep-link as a push tap).
 */
const NotificationsScreen: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const items = useSelector((state: RootState) => state.notifications?.items ?? []);
  const unreadCount = useSelector((state: RootState) => state.notifications?.unreadCount ?? 0);
  const isLoading = useSelector((state: RootState) => state.notifications?.isLoading ?? false);

  const load = useCallback(() => {
    dispatch(getNotifications());
    dispatch(getUnreadCount()); // authoritative count (list is capped at 50)
  }, [dispatch]);

  useFocusEffect(load);

  const onTap = (n: NotificationItem) => {
    dispatch(markNotificationRead(n.id));
    if (n.actionData) {
      try {
        dispatchNotificationRoute(JSON.parse(n.actionData), true);
      } catch {
        /* unroutable payload — the mark-read still stands */
      }
    }
  };

  return (
    <View style={styles.screen}>
      {unreadCount > 0 && (
        <View style={styles.headerRow}>
          <Text style={styles.headerCount}>{unreadCount} unread</Text>
          <TouchableOpacity onPress={() => dispatch(markAllNotificationsRead())}>
            <Text style={styles.markAll}>Mark all read</Text>
          </TouchableOpacity>
        </View>
      )}
      <ScrollView
        contentContainerStyle={styles.content}
        refreshControl={
          <RefreshControl
            refreshing={isLoading}
            onRefresh={load}
            tintColor={colors.accent}
            colors={[colors.accent]}
          />
        }>
        {items.length === 0 ? (
          <View style={styles.empty}>
            <View style={styles.emptyMark}>
              <Icon name="notifications-outline" size={30} color={colors.inkFaint} />
            </View>
            <Text style={styles.emptyTitle}>No notifications</Text>
            <Text style={styles.emptyBody}>Cover requests and updates will show up here.</Text>
          </View>
        ) : (
          items.map((n) => (
            <TouchableOpacity
              key={n.id}
              style={[styles.card, !n.read && styles.cardUnread]}
              activeOpacity={0.7}
              onPress={() => onTap(n)}>
              <View style={[styles.iconWrap, !n.read && styles.iconWrapUnread]}>
                <Icon
                  name={iconFor(n.type)}
                  size={18}
                  color={!n.read ? colors.accent : colors.inkFaint}
                />
              </View>
              <View style={styles.cardBody}>
                <Text style={[styles.cardTitle, !n.read && styles.cardTitleUnread]}>{n.title}</Text>
                <Text style={styles.cardMessage}>{n.message}</Text>
                <Text style={styles.cardTime}>{relTime(n.createdOn)}</Text>
              </View>
              {!n.read && <View style={styles.unreadDot} />}
            </TouchableOpacity>
          ))
        )}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: colors.bg },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: space.lg,
    paddingTop: space.md,
  },
  headerCount: { fontFamily: font.bodySemi, fontSize: 14, color: colors.inkSoft },
  markAll: { fontFamily: font.bodySemi, fontSize: 14, color: colors.accent },
  content: { padding: space.lg, paddingTop: space.md },
  card: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: colors.surface,
    borderRadius: radius.md,
    padding: space.md,
    marginBottom: space.sm,
    borderWidth: 1,
    borderColor: colors.line,
  },
  cardUnread: { borderColor: colors.accentSoft, ...shadow.card },
  iconWrap: {
    width: 36,
    height: 36,
    borderRadius: radius.pill,
    backgroundColor: colors.surfaceSunk,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: space.md,
  },
  iconWrapUnread: { backgroundColor: colors.accentSoft },
  cardBody: { flex: 1 },
  cardTitle: { fontFamily: font.bodySemi, fontSize: 15, color: colors.ink },
  cardTitleUnread: { fontFamily: font.bodyBold },
  cardMessage: { fontFamily: font.body, fontSize: 14, color: colors.inkSoft, marginTop: 2 },
  cardTime: { fontFamily: font.body, fontSize: 12, color: colors.inkFaint, marginTop: 6 },
  unreadDot: {
    width: 9,
    height: 9,
    borderRadius: radius.pill,
    backgroundColor: colors.accent,
    marginLeft: space.sm,
    marginTop: 4,
  },
  empty: { alignItems: 'center', paddingVertical: space.xxl },
  emptyMark: {
    width: 60,
    height: 60,
    borderRadius: radius.pill,
    backgroundColor: colors.surfaceSunk,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: space.md,
  },
  emptyTitle: { fontFamily: font.bodyBold, fontSize: 16, color: colors.ink, marginBottom: 4 },
  emptyBody: {
    fontFamily: font.body,
    fontSize: 14,
    color: colors.inkFaint,
    textAlign: 'center',
    paddingHorizontal: space.xl,
  },
});

export default NotificationsScreen;
